import { decodeHTML } from 'entities';
import moment from 'moment';
import { Fragment, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { postPAJAL010, postPAJAL010Delete } from '../../../services/Api';
import Pagination from '../../../shared/components/Pagination';
import PopupSendMail from '../../../shared/components/PopupSendMail';
import SearchDateRange from '../../../shared/components/SearchDateRange';
import SideNav from '../../../shared/components/SideNav';
import { MESSAGE } from '../../../shared/constants/app';

const TAB_LIST = [
    { label: '전체', key: 'totalCount', value: 'ALL' },
    { label: '열람중', key: 'activeCount', value: 'ACTIVE' },
    { label: '기간만료', key: 'expiredCount', value: 'EXPIRED' },
];

// 열람권 종류별 안내문구 (이력서 삭제/비공개)
const BLOCKED_STATUS = {
    RS_COUNT: {
        delete: {
            label: '삭제',
            lines: ['열람하신 후 24시간 이내에 이력서가 삭제되면 사용된 열람권이 반환됩니다'],
        },
        disable: {
            label: '비공개',
            lines: ['열람하신 후 24시간 이내에', '이력서가 비공개로 전환되면', '사용된 열람권이 반환됩니다'],
        },
    },
    RS_PERIOD: {
        delete: {
            label: '삭제',
            lines: ['인재가 이력서를 삭제하였습니다'],
        },
        disable: {
            label: '비공개',
            lines: ['인재가 이력서를', '비공개로 전환하였습니다'],
        },
    },
};

const getBlockedStatus = (item) => {
    if (item?.isExpired) return null;

    const productCode = item?.productCode || '';
    const productType = Object.keys(BLOCKED_STATUS).find((prefix) => productCode.startsWith(prefix));
    if (!productType) return null;

    if (item?.isDelete === 'Y') return BLOCKED_STATUS[productType].delete;
    if (item?.isDisable === 'Y') return BLOCKED_STATUS[productType].disable;
    return null;
};

const renderRemainTime = (remainTime) =>
    (remainTime || '').split(/(\d+)/).map((part, index) =>
        /\d+/.test(part) ? (
            <span className="cv-viewed-list__time-number" key={`${part}-${index}`}>
                {part}
            </span>
        ) : (
            part
        ),
    );

// map giống cột 1 của PAFAA010: 경력 | 근무형태 | 지역
const getResumeInfo = (item) => {
    const career =
        item?.resumeCareer === '001' ? '신입' : `경력${item?.resumeCareerYear ? `(${item.resumeCareerYear})` : ''}`;
    const form =
        item?.resumeForm && item.resumeForm.includes(',') ? `${item.resumeForm.split(',')[0]} 외` : item?.resumeForm;
    const regions = [
        { si: item?.resumeRegionSi1, gu: item?.resumeRegionGu1 },
        { si: item?.resumeRegionSi2, gu: item?.resumeRegionGu2 },
        { si: item?.resumeRegionSi3, gu: item?.resumeRegionGu3 },
    ]
        .filter((region) => region?.si && region?.gu)
        .map((region) => `${region.si} > ${region.gu}`)
        .join(', ');

    return [career, form, regions].filter(Boolean).join('  |  ');
};

const PAJAL010 = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const page = Number(searchParams.get('page')) || 1;
    const [pages, setPages] = useState({});
    const [target, setTarget] = useState('ALL');
    const [countTab, setCountTab] = useState({});
    const [list, setList] = useState([]);
    const [receiver, setReceiver] = useState(null);
    const [dataStartDate, setDataStartDate] = useState('');
    const [dataEndDate, setDataEndDate] = useState('');

    const getListData = (startDate, endDate) => {
        const startDateFormat = startDate ? moment(startDate).format('YYYY-MM-DD') : null;
        const endDateFormat = endDate ? moment(endDate).format('YYYY-MM-DD') : null;
        postPAJAL010({
            page,
            target,
            startDate: startDateFormat,
            endDate: endDateFormat,
        })
            .then(({ data }) => {
                setCountTab(data?.data?.countTab || {});
                setList(Array.isArray(data?.data?.list) ? data.data.list : []);
                setPages(data?.info || {});
            })
            .catch((error) => console.log(error));
    };

    const handleSearchDateRange = (startDate, endDate) => {
        setDataStartDate(startDate);
        setDataEndDate(endDate);
        getListData(startDate, endDate);
    };

    const handleTabClick = (value) => {
        if (value === target) return;
        setTarget(value);
        setSearchParams({ page: 1 });
    };

    useEffect(() => {
        getListData(dataStartDate, dataEndDate);
        // eslint-disable-next-line
    }, [page, target]);

    const handleDelete = async (unlockSeq) => {
        if (!unlockSeq) return;
        if (!window.confirm('삭제하시겠습니까?')) return;

        try {
            await postPAJAL010Delete(unlockSeq);
            getListData(dataStartDate, dataEndDate);
            window.alert(MESSAGE.notify.deleteSuccess);
        } catch (error) {
            console.error(error);
            window.alert(MESSAGE.notify.deleteError);
        }
    };

    const handleSendMessage = (item) => {
        setReceiver({ id: item?.resumeMemberID, name: item?.nameKor });
    };

    return (
        <>
            <div id="content" className="sub-page">
                <div className="container">
                    <div id="top-sub-page">
                        <SideNav />
                        <div className="resume">
                            <div className="resume__inner">
                                <h3 className="resume__tit">열람권 사용 현황</h3>
                                <div className="cv-viewed-list">
                                    <ul className="rsm-stt__list cv-viewed-list__summary">
                                        <li className="rsm-stt__item cv-viewed-list__summary-item">
                                            <span>열람중</span>
                                            <div className="rsm-stt__num cv-viewed-list__summary-num">
                                                {countTab?.activeCount || 0}
                                            </div>
                                        </li>
                                        <li className="rsm-stt__item cv-viewed-list__summary-item">
                                            <span>기간 만료</span>
                                            <div className="rsm-stt__num cv-viewed-list__summary-num">
                                                {countTab?.expiredCount || 0}
                                            </div>
                                        </li>
                                    </ul>
                                    <SearchDateRange
                                        className="cv-viewed-list__search"
                                        handleSearchDateRange={handleSearchDateRange}
                                        setSearchParams={setSearchParams}
                                    />
                                    <div className="mtch__block mtch__block--rp3 cv-viewed-list__tabs">
                                        {TAB_LIST.map((tab) => (
                                            <button
                                                type="button"
                                                key={tab.value}
                                                className={`mtch__cnd ${target === tab.value ? 'active' : ''}`}
                                                onClick={() => handleTabClick(tab.value)}
                                            >
                                                {tab.label}({countTab?.[tab.key] || 0})
                                            </button>
                                        ))}
                                    </div>
                                    <div className="cv-viewed-list__head">
                                        <span>내용</span>
                                        <span>날짜</span>
                                        <span>기능</span>
                                        <span>관리</span>
                                    </div>
                                    <div className="cv-viewed-list__items">
                                        {list.length > 0 ? (
                                            list.map((item, index) => {
                                                const blockedStatus = getBlockedStatus(item);

                                                return (
                                                    <div
                                                        className={`cv-viewed-list__item ${item?.isExpired ? 'is-expired' : ''}`}
                                                        key={item?.unlockSeq || index}
                                                    >
                                                        <div className="cv-viewed-list__content">
                                                            <strong>{item?.nameKor}</strong>
                                                            <p>
                                                                {item?.isExpired || blockedStatus ? (
                                                                    decodeHTML(item?.title || '')
                                                                ) : (
                                                                    <a
                                                                        href={`/pafaa060-${item?.resumeSeq}`}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                    >
                                                                        {decodeHTML(item?.title || '')}
                                                                    </a>
                                                                )}
                                                            </p>
                                                            <span>{getResumeInfo(item)}</span>
                                                        </div>
                                                        <div className="cv-viewed-list__date">
                                                            <span>
                                                                열람일
                                                                <br />
                                                                {item?.unlockDate}
                                                            </span>
                                                            <span>
                                                                최종수정
                                                                <br />
                                                                {item?.modDate
                                                                    ? moment(item.modDate).format('YY.MM.DD')
                                                                    : ''}
                                                                <br />
                                                                {item?.modDate
                                                                    ? moment(item.modDate).format('HH:mm:ss')
                                                                    : ''}
                                                            </span>
                                                        </div>
                                                        <div className="cv-viewed-list__function">
                                                            {item?.isExpired ? (
                                                                <strong className="is-expired">기간만료</strong>
                                                            ) : blockedStatus ? (
                                                                <>
                                                                    <strong className="cv-viewed-list__blocked">
                                                                        {blockedStatus.label}
                                                                    </strong>
                                                                    <p className="cv-viewed-list__blocked-desc">
                                                                        {blockedStatus.lines.map((line, lineIndex) => (
                                                                            <Fragment key={line}>
                                                                                {lineIndex > 0 && <br />}
                                                                                {line}
                                                                            </Fragment>
                                                                        ))}
                                                                    </p>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <span>열람 가능 시간</span>
                                                                    <div className="cv-viewed-list__time">
                                                                        {renderRemainTime(item?.remainingTimeStatus)}
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        className="cv-viewed-list__message"
                                                                        onClick={() => handleSendMessage(item)}
                                                                    >
                                                                        쪽지 보내기
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                        <div className="cv-viewed-list__manage">
                                                            <button
                                                                type="button"
                                                                className="msg__delete"
                                                                onClick={() => handleDelete(item?.unlockSeq)}
                                                                aria-label={`${item?.nameKor} 삭제`}
                                                            >
                                                                <i className="icon icon-trash-2"></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="cv-viewed-list__empty">데이터없습니다</div>
                                        )}
                                    </div>
                                    <Pagination pages={pages} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {receiver && <PopupSendMail closePopup={() => setReceiver(null)} receiver={receiver} />}
        </>
    );
};

export default PAJAL010;
